import { useEffect, useMemo } from "react";
import { Client } from "urql";
import PlaylistData from "../../playlist/data/PlaylistData";
import { CarouselPosition } from "../../carousel/useCarouselPosition";
import { useCrewLiveContext } from "../CrewLiveContext";
import { useDeviceContext } from "../../device/DeviceInfoContext";
import { pushDiscovery } from "../discover/DiscoveryPusher";
import { pushPlaylistLivePosition } from "./PlaylistLivePositionPusher";
import { PlaylistLivePositionPayload } from "./PlaylistLivePositionPayload";

interface Props {
  graphQlClient: Client;
  playlistIdentifier: string;
  playlistData: PlaylistData;
  isBeamerModeActive: boolean;
  listOfFollowedDevices: readonly string[];
  position: CarouselPosition;
}

/**
 * Pushes the own carousel position to the crew, answers discover requests and follows the most
 * recent position of the followed devices.
 *
 * @returns the live positions of all devices in this playlist
 */
export function usePlaylistLivePositionSync({
  graphQlClient,
  playlistIdentifier,
  playlistData,
  isBeamerModeActive,
  listOfFollowedDevices,
  position: { activeItem, pageInItem, activeLyricsPart, goToItem },
}: Props): readonly PlaylistLivePositionPayload[] {
  const { deviceIdentifier, deviceReadableName, deviceColor } =
    useDeviceContext();
  const {
    discoverDependencyCounter,
    currentCrewIdentifier,
    playlistLivePosition,
    playlistLivePositionEventSourceHandlerIsReady,
  } = useCrewLiveContext();

  const viewMode = isBeamerModeActive ? "lyrics" : "songsheet";
  const activeItemId = activeItem?.id;

  const positionsInPlaylist = useMemo(
    () =>
      Array.from(playlistLivePosition.values()).filter(
        (p) => p.playlistIdentifier === playlistIdentifier,
      ),
    [playlistLivePosition, playlistIdentifier],
  );

  useEffect(() => {
    if (
      currentCrewIdentifier === undefined ||
      !playlistLivePositionEventSourceHandlerIsReady
    )
      return;

    pushDiscovery(graphQlClient, deviceIdentifier, currentCrewIdentifier);
  }, [
    currentCrewIdentifier,
    playlistLivePositionEventSourceHandlerIsReady,
    graphQlClient,
    deviceIdentifier,
  ]);

  useEffect(() => {
    if (
      currentCrewIdentifier === undefined ||
      !playlistLivePositionEventSourceHandlerIsReady
    )
      return;

    console.debug(
      `Calling pushPlaylistLivePosition. discoverDependencyCounter=${discoverDependencyCounter}`,
    );
    pushPlaylistLivePosition(graphQlClient, currentCrewIdentifier, {
      deviceIdentifier,
      deviceReadableName,
      deviceColor,
      playlistIdentifier,
      timestamp: Date.now(),
      selectedPlaylistItemInMemoryId: activeItemId,
      viewMode,
      currentLyricsPosition: activeLyricsPart,
      pageInItem,
    });
  }, [
    activeItemId,
    pageInItem,
    activeLyricsPart,
    viewMode,
    currentCrewIdentifier,
    playlistLivePositionEventSourceHandlerIsReady,
    graphQlClient,
    playlistIdentifier,
    deviceIdentifier,
    deviceReadableName,
    deviceColor,
    discoverDependencyCounter, //this dependency makes sure, that we handle the discover request
  ]);

  useEffect(() => {
    const optHead = positionsInPlaylist
      .filter((p) => listOfFollowedDevices.includes(p.deviceIdentifier))
      .sort((a, b) => a.timestamp - b.timestamp)
      .pop();
    if (optHead === undefined) return;

    const item = playlistData
      .getItems()
      .find((i) => i.id === optHead.selectedPlaylistItemInMemoryId);
    if (item === undefined) return;

    console.debug("following playlistLivePosition", optHead);
    //page and lyrics part only make sense if both devices show the same kind of slides
    if (optHead.viewMode === viewMode) {
      goToItem(
        item,
        optHead.pageInItem,
        viewMode === "lyrics" ? optHead.currentLyricsPosition : undefined,
      );
    } else {
      goToItem(item);
    }
  }, [
    positionsInPlaylist,
    listOfFollowedDevices,
    playlistData,
    viewMode,
    goToItem,
  ]);

  return positionsInPlaylist;
}
